import { useEffect, useState } from "react";
import { BasketItem, setBasketItem } from "../../models/BasketItem";
import useApiRequest from "../../../utils/hooks/useApiRequest";
import {
  ADD_EXERCISE_TO_BASKET,
  ADD_TO_BASKET,
  CLEAR_BASKET,
  FACILITY_IDS,
  GET_BASKET,
  REMOVE_FROM_BASKET,
  REMOVE_FROM_WHOLE_BASKET
} from "../../api/apiRoutes";
import { Assort } from "../../models/Assort";
import { Exercise } from "../../models/Exercise";
import getId from "../../../utils/getId";
import useTypeSale from "../../../utils/hooks/useTypeSale";
import { addToCart, beginCheckout, removeFromCart } from "../../ga4/gaMethods";
import { useLocation, useNavigate } from "react-router";
import { EXERCISE_PAGE, FINALIZE_PAGE, REALIZE_VOUCHER_PAGE, TICKETS_PAGE } from "../../routes";
import qs from "qs";
import { REALIZE_VOUCHER } from "../../models/TypeSale";

export default function useBasketManager(isSaleWithExercise: boolean, assorts?: Assort[], isChangeZone?: boolean) {
  const { get, post } = useApiRequest();
  const [basket, setBasket] = useState<BasketItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { categoryBrand } = useTypeSale();
  const [totalListPrice, setTotalListPrice] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const typeSale = qs.parse(location.search.slice(1)).typeSale as string;

  const getBasket = async () => {
    setLoading(true);
    const { result, isError } = await get(GET_BASKET);
    if (!isError) {
      setBasket(result.basket);
      setTotalListPrice(result.totalListPrice);
      if (!isChangeZone) {
        validateBasket(result.basket);
      }
      if (location.pathname === FINALIZE_PAGE && result.basket.length > 0) {
        const coupon = basket.find(item => item.promotionCode !== "")?.promotionCode;
        beginCheckout(result.basket, categoryBrand, coupon ? coupon : "");
      }
    }
    setLoading(false);
  };

  const calculateBasketTotal = (cart: BasketItem[]) => {
    return cart.reduce((acc: number, item: BasketItem) => acc + item.price * item.qnt, 0);
  };

  const addToBasket = async (assort: Assort) => {
    setLoading(true);
    const { result, isError } = await post(ADD_TO_BASKET, setBasketItem(assort, FACILITY_IDS));
    if (!isError) {
      setBasket(result);
      addToCart(result, categoryBrand, calculateBasketTotal(result));
    }
    setLoading(false);
  };

  const removeBasket = async (idx: number) => {
    setLoading(true);
    const removeItems = basket.filter((item, index) => index === idx);
    const { result, isError } = await post(REMOVE_FROM_BASKET, { idx });
    if (!isError) {
      removeFromCart(removeItems, categoryBrand, calculateBasketTotal(removeItems));
      setBasket(result.basket);
      setTotalListPrice(result.totalListPrice);
      returnWhenBasketIsEmpty(result.basket);
    }
    setLoading(false);
  };

  const removeWholeBasket = async (idx: number) => {
    setLoading(true);
    const removeItems = basket.filter((item, index) => index === idx);
    const { result, isError } = await post(REMOVE_FROM_WHOLE_BASKET, { idx });
    if (!isError) {
      removeFromCart(removeItems, categoryBrand, calculateBasketTotal(removeItems));
      setBasket(result.basket);
      setTotalListPrice(result.totalListPrice);
      returnWhenBasketIsEmpty(result.basket);
    }
    setLoading(false);
  };

  const addExerciseToBasket = async (exercise: Exercise) => {
    setLoading(true);
    const { result, isError } = await post(ADD_EXERCISE_TO_BASKET(getId(exercise)));
    if (!isError) {
      setBasket(result.basket);
    }
    setLoading(false);
  };

  const disabledFinalizeBtn = (): boolean => {
    return basket.some((item: BasketItem) => item.ticketAccess.length === 0);
  };

  const returnWhenBasketIsEmpty = (basket: BasketItem[]) => {
    if (location.pathname === EXERCISE_PAGE) {
      if (typeSale === REALIZE_VOUCHER) {
        if (basket.length === 0) navigate(`${REALIZE_VOUCHER_PAGE}?isSaleWithExercise=true`);
      } else {
        if (basket.length === 0) navigate(`${TICKETS_PAGE}?isSaleWithExercise=${isSaleWithExercise}`);
      }
    }
  };

  const clearBasket = async () => {
    const { isError } = await post(CLEAR_BASKET);
    if (!isError) {
      setBasket([]);
    }
  };

  const validateBasket = async (valueBasket: BasketItem[]) => {
    if (assorts && assorts.length > 0) {
      const listBasketIndex = valueBasket.map((item: BasketItem, index1: number) => {
        const index2 = assorts.findIndex((assort: Assort) => getId(assort) === item.assortIds);
        return index2 >= 0 ? index1 : -1;
      });

      const listIndexRemove = listBasketIndex.filter(index => index >= 0);
      const listIndexNotRemove = listBasketIndex.filter(index => index === -1);

      if (listIndexRemove.length === 0 && listIndexNotRemove.length > 0) {
        clearBasket();
      }
    }
  };

  useEffect(() => {
    getBasket();
    //eslint-disable-next-line
  }, [assorts]);

  return {
    loading,
    basket,
    setBasket,
    addToBasket,
    removeBasket,
    addExerciseToBasket,
    disabledFinalizeBtn,
    categoryBrand,
    totalListPrice,
    removeWholeBasket,
    clearBasket
  };
}
